import type Customer from 'types/customer';
import { getCell, toBool, toFloat, toInt } from './xlsxImportShared';

export const CLIENT_COL = {
    name: 'שם לקוח',
    businessId: 'מס עוסק',
    businessName: 'שם חברה',
    includeTax: 'כולל מעמ',
    city: 'עיר',
    internalId: 'מזהה',
    active: 'פעיל',
    agent: 'סוכן',
    gps: 'נקודת ציון',
    driver: 'נהג',
    phone2: 'טלפון 2',
    contact2: 'איש קשר 2',
    phone3: 'טלפון 3',
    contact3: 'איש קשר 3',
    street: 'רחוב',
    streetNumber: 'מספר רחוב',
    phone: 'טלפון',
    contact: 'איש קשר',
    priceControl: 'מחירון',
    email: 'אימייל',
    taxNoteType: 'תעודות',
    discount: 'הנחה קבועה',
    externalId: 'מזהה חיצוני',
    position: 'מיקום',
    comments: 'הערות',
    minOrder: 'מינימום',
    category: 'קטגוריה',
    master: 'מרכזי',
    paymentComments: 'תנאי תשלום',
    branch: 'סניף',
    group: 'קטגוריה 2',
    obligo: 'אובליגו',
    days: 'ימים',
    minOrderMoney: 'מינימום הזמנה בכסף',
    daysToPay: 'שוטף',
} as const;

/** Hebrew day letters (א..ש) → weekday flags, aligned with server serializeDays bit order */
function parseHebrewDays(s: string | undefined): Partial<Pick<Customer, 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday'>> | null {
    if (s == null || String(s).trim() === '') return null;
    const t = String(s).trim();
    if (/^\d+$/.test(t)) {
        const n = parseInt(t, 10);
        if (Number.isNaN(n)) return null;
        return {
            sunday: Boolean(n & 1),
            monday: Boolean(n & 2),
            tuesday: Boolean(n & 4),
            wednesday: Boolean(n & 8),
            thursday: Boolean(n & 16),
            friday: Boolean(n & 32),
            saturday: Boolean(n & 64),
        };
    }
    const set = t.split(/[,;]/).map((x) => x.trim());
    const letters = ['א', 'ב', 'ג', 'ד', 'ה', 'ו', 'ש'];
    const out: Record<string, boolean> = { sunday: false, monday: false, tuesday: false, wednesday: false, thursday: false, friday: false, saturday: false };
    const keys: (keyof typeof out)[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    letters.forEach((letter, i) => {
        if (set.includes(letter)) out[keys[i]] = true;
    });
    return out;
}

export function findClient(customers: Customer[], internalId: number | undefined, extId: string | undefined): Customer | undefined {
    if (internalId != null && !Number.isNaN(internalId) && internalId > 0) {
        const c = customers.find((x) => String(x.id) === String(internalId) || Number(x.id) === internalId);
        if (c) return c;
    }
    if (extId != null && String(extId).trim() !== '') {
        return customers.find((x) => String(x.externalId ?? '').trim() === String(extId).trim());
    }
    return undefined;
}

export function rowToPartial(row: unknown[], cols: Record<string, number> | undefined): {
    name?: string;
    idNum?: number;
    externalIdStr?: string;
    patch: Partial<Customer>;
} {
    const n = getCell(row, cols, CLIENT_COL.name);
    const idStr = getCell(row, cols, CLIENT_COL.internalId);
    const idNum = toInt(idStr);
    const ext = getCell(row, cols, CLIENT_COL.externalId);
    const patch: Partial<Customer> = {};
    if (n) patch.name = n;
    if (ext) patch.externalId = ext;
    {
        const v = getCell(row, cols, CLIENT_COL.businessId);
        if (v) patch.businessId = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.businessName);
        if (v) patch.businessName = v;
    }
    {
        const v = toBool(getCell(row, cols, CLIENT_COL.includeTax));
        if (v !== undefined) patch.includeTax = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.city);
        if (v) patch.city = v;
    }
    {
        const v = toInt(getCell(row, cols, CLIENT_COL.active));
        if (v !== undefined) patch.active = v === 1;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.agent);
        if (v) patch.agent = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.gps);
        if (v) patch.location = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.driver);
        if (v) patch.driver = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.phone2);
        if (v) patch.phone2 = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.contact2);
        if (v) patch.contactName2 = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.phone3);
        if (v) patch.phone3 = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.contact3);
        if (v) patch.contactName3 = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.street);
        if (v) patch.street = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.streetNumber);
        if (v) patch.streetNumber = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.phone);
        if (v) patch.phone = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.contact);
        if (v) patch.contactName = v;
    }
    {
        const v = toInt(getCell(row, cols, CLIENT_COL.priceControl));
        if (v !== undefined) patch.priceControl = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.email);
        if (v) patch.email = v;
    }
    {
        const v = toInt(getCell(row, cols, CLIENT_COL.taxNoteType));
        if (v !== undefined) patch.tax_note_type = v;
    }
    {
        const v = toFloat(getCell(row, cols, CLIENT_COL.discount));
        if (v !== undefined) patch.discount = v;
    }
    {
        const v = toInt(getCell(row, cols, CLIENT_COL.position));
        if (v !== undefined) patch.position = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.comments);
        if (v) patch.comments = v;
    }
    {
        const v = toInt(getCell(row, cols, CLIENT_COL.minOrder));
        if (v !== undefined) patch.min_order = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.category);
        if (v) patch.category = v;
    }
    {
        const v = toInt(getCell(row, cols, CLIENT_COL.master));
        if (v !== undefined) (patch as any).master = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.paymentComments);
        if (v) patch.paymentComments = v;
    }
    {
        const v = toInt(getCell(row, cols, CLIENT_COL.branch));
        if (v !== undefined) (patch as any).branch = v;
    }
    {
        const v = getCell(row, cols, CLIENT_COL.group);
        if (v) patch.group = v;
    }
    {
        const v = toFloat(getCell(row, cols, CLIENT_COL.obligo));
        if (v !== undefined) patch.obligo = v;
    }
    {
        const v = parseHebrewDays(getCell(row, cols, CLIENT_COL.days));
        if (v) Object.assign(patch, v);
    }
    {
        const v = toFloat(getCell(row, cols, CLIENT_COL.minOrderMoney));
        if (v !== undefined) patch.min_order_sum = v;
    }
    {
        const v = toInt(getCell(row, cols, CLIENT_COL.daysToPay));
        if (v !== undefined) patch.days_to_pay = v;
    }
    return {
        name: n || undefined,
        idNum: idNum != null && !Number.isNaN(idNum) && idNum > 0 ? idNum : undefined,
        externalIdStr: ext && ext.trim() !== '' ? ext : undefined,
        patch,
    };
}

export function applyPatch(base: Customer, partial: Partial<Customer>): Customer {
    return { ...base, ...partial, id: base.id, nkObject: base.nkObject } as Customer;
}
